import type { SceneNode } from "@/document/model/nodes";
import { rotatePoint, transformRotationOrigin } from "@/lib/math/rotation";
import { primitiveToPolygon } from "./primitiveGeometry";
import { sampleNetworkContours } from "./vectorGeometry";

export interface Point {
  x: number;
  y: number;
}

export function sceneNodeToContours(node: SceneNode): Point[][] {
  const transformed = (contours: Point[][]): Point[][] => transformNodeContours(node, contours);

  if (node.type === "group" || node.type === "component" || node.type === "componentVariant" || node.type === "instance") {
    return transformed(node.children.flatMap(child => sceneNodeToContours(child)));
  }

  if (node.type === "frame") {
    return transformed(node.children.flatMap(child => sceneNodeToContours(child)));
  }

  if (node.type === "primitive") {
    const polygon = primitiveToPolygon(node);
    return transformed(polygon.length >= 3 ? [polygon] : []);
  }

  if (node.type === "vectorNetwork") {
    return transformed(sampleNetworkContours(node.network).filter(contour => contour.length >= 3));
  }

  if (node.type === "shape") {
    return transformed(pathDataToContours(node.path));
  }

  if (node.type === "boolean") {
    return transformed(node.resolved.contours.map(contour => contour.map(point => ({ ...point }))));
  }

  return [];
}

function transformNodeContours(node: SceneNode, contours: Point[][]): Point[][] {
  const rotation = node.transform.rotation;
  if (!Number.isFinite(rotation) || rotation === 0 || contours.length === 0) {
    return contours;
  }

  const origin = transformRotationOrigin(node.bounds, node.transform);
  return contours.map(contour => contour.map(point => rotatePoint(point, origin, rotation)));
}

export function pathDataToContours(pathData: string, curveSteps = 24): Point[][] {
  const tokens = tokenizePath(pathData);
  const contours: Point[][] = [];
  let contour: Point[] = [];
  let cursor: Point = { x: 0, y: 0 };
  let contourStart: Point | undefined;
  let command = "";
  let index = 0;

  const readNumber = () => Number(tokens[index++] ?? 0);
  const hasNumber = () => index < tokens.length && !isPathCommand(tokens[index]!);

  while (index < tokens.length) {
    if (isPathCommand(tokens[index]!)) {
      command = tokens[index++]!;
    }

    switch (command) {
      case "M":
      case "m": {
        const relative = command === "m";
        while (hasNumber()) {
          const point = absolutize({ x: readNumber(), y: readNumber() }, cursor, relative);
          if (contour.length >= 3) {
            contours.push(contour);
          }
          contour = [point];
          cursor = point;
          contourStart = point;
          command = relative ? "l" : "L";
        }
        break;
      }
      case "L":
      case "l": {
        const relative = command === "l";
        while (hasNumber()) {
          cursor = absolutize({ x: readNumber(), y: readNumber() }, cursor, relative);
          contour.push(cursor);
        }
        break;
      }
      case "H":
      case "h": {
        const relative = command === "h";
        while (hasNumber()) {
          const x = readNumber();
          cursor = { x: relative ? cursor.x + x : x, y: cursor.y };
          contour.push(cursor);
        }
        break;
      }
      case "V":
      case "v": {
        const relative = command === "v";
        while (hasNumber()) {
          const y = readNumber();
          cursor = { x: cursor.x, y: relative ? cursor.y + y : y };
          contour.push(cursor);
        }
        break;
      }
      case "C":
      case "c": {
        const relative = command === "c";
        while (hasNumber()) {
          const p0 = cursor;
          const p1 = absolutize({ x: readNumber(), y: readNumber() }, cursor, relative);
          const p2 = absolutize({ x: readNumber(), y: readNumber() }, cursor, relative);
          const p3 = absolutize({ x: readNumber(), y: readNumber() }, cursor, relative);
          for (let step = 1; step <= curveSteps; step += 1) {
            contour.push(evaluateCubic(p0, p1, p2, p3, step / curveSteps));
          }
          cursor = p3;
        }
        break;
      }
      case "Q":
      case "q": {
        const relative = command === "q";
        while (hasNumber()) {
          const p0 = cursor;
          const p1 = absolutize({ x: readNumber(), y: readNumber() }, cursor, relative);
          const p2 = absolutize({ x: readNumber(), y: readNumber() }, cursor, relative);
          for (let step = 1; step <= curveSteps; step += 1) {
            contour.push(evaluateQuadratic(p0, p1, p2, step / curveSteps));
          }
          cursor = p2;
        }
        break;
      }
      case "Z":
      case "z": {
        if (contourStart) {
          cursor = contourStart;
        }
        if (contour.length >= 3) {
          contours.push(contour);
        }
        contour = [];
        contourStart = undefined;
        command = "";
        break;
      }
      default:
        index += 1;
    }
  }

  if (contour.length >= 3) {
    contours.push(contour);
  }

  return contours.map(removeDuplicatePoints).filter(contour => contour.length >= 3 && Math.abs(signedArea(contour)) > 0.001);
}

export function contoursToPathData(contours: Point[][]): string {
  return contours
    .filter(contour => contour.length >= 3)
    .map(contour => {
      const [start, ...points] = contour;
      return [
        `M ${formatNumber(start!.x)} ${formatNumber(start!.y)}`,
        ...points.map(point => `L ${formatNumber(point.x)} ${formatNumber(point.y)}`),
        "Z",
      ].join(" ");
    })
    .join(" ");
}

export function boundsFromContours(contours: Point[][]): { x: number; y: number; width: number; height: number } {
  const points = contours.flat();
  if (points.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const left = Math.min(...points.map(point => point.x));
  const top = Math.min(...points.map(point => point.y));
  const right = Math.max(...points.map(point => point.x));
  const bottom = Math.max(...points.map(point => point.y));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

export function transformContours(
  contours: Point[][],
  fromBounds: { x: number; y: number; width: number; height: number },
  toBounds: { x: number; y: number; width: number; height: number },
): Point[][] {
  const scaleX = fromBounds.width === 0 ? 1 : toBounds.width / fromBounds.width;
  const scaleY = fromBounds.height === 0 ? 1 : toBounds.height / fromBounds.height;
  return contours.map(contour => contour.map(point => ({
    x: toBounds.x + (point.x - fromBounds.x) * scaleX,
    y: toBounds.y + (point.y - fromBounds.y) * scaleY,
  })));
}

export function transformPathData(
  pathData: string,
  fromBounds: { x: number; y: number; width: number; height: number },
  toBounds: { x: number; y: number; width: number; height: number },
): string {
  const scaleX = fromBounds.width === 0 ? 1 : toBounds.width / fromBounds.width;
  const scaleY = fromBounds.height === 0 ? 1 : toBounds.height / fromBounds.height;
  const tokens = tokenizePath(pathData);
  const commands: string[] = [];
  let command = "";
  let index = 0;

  const transformPoint = (x: number, y: number): Point => ({
    x: toBounds.x + (x - fromBounds.x) * scaleX,
    y: toBounds.y + (y - fromBounds.y) * scaleY,
  });
  const readNumber = () => Number(tokens[index++] ?? 0);
  const hasNumber = () => index < tokens.length && !isPathCommand(tokens[index]!);

  while (index < tokens.length) {
    if (isPathCommand(tokens[index]!)) {
      command = tokens[index++]!;
    }

    switch (command) {
      case "M":
      case "L":
      case "Q":
      case "C": {
        const valuesPerCommand = command === "C" ? 6 : command === "Q" ? 4 : 2;
        while (hasNumber()) {
          const values = Array.from({ length: valuesPerCommand }, readNumber);
          const transformed = [];
          for (let valueIndex = 0; valueIndex < values.length; valueIndex += 2) {
            const point = transformPoint(values[valueIndex]!, values[valueIndex + 1]!);
            transformed.push(formatNumber(point.x), formatNumber(point.y));
          }
          commands.push(`${command} ${transformed.join(" ")}`);
        }
        break;
      }
      case "Z":
      case "z":
        commands.push("Z");
        command = "";
        break;
      default:
        index += 1;
    }
  }

  return commands.join(" ");
}

export function pointInContours(point: Point, contours: Point[][]): boolean {
  return contours.reduce((inside, contour) => pointInPolygon(point, contour) ? !inside : inside, false);
}

export function contourPointWindingNumber(point: Point, contour: Point[]): number {
  let winding = 0;
  for (let index = 0; index < contour.length; index += 1) {
    const current = contour[index]!;
    const next = contour[(index + 1) % contour.length]!;
    if (current.y <= point.y) {
      if (next.y > point.y && direction(current, next, point) > 0) {
        winding += 1;
      }
    } else if (next.y <= point.y && direction(current, next, point) < 0) {
      winding -= 1;
    }
  }

  return winding;
}

export function pointNonzeroInContours(point: Point, contours: Point[][]): boolean {
  return contours.reduce((winding, contour) => winding + contourPointWindingNumber(point, contour), 0) !== 0;
}

export function pointFilledInContours(point: Point, contours: Point[][], fillRule: "nonzero" | "evenodd"): boolean {
  const normalizedContours = normalizeNestedContourWindings(contours);
  const nestingChain = nestingChainAtPoint(point, normalizedContours);
  if (nestingChain.length > 0) {
    return nestingChain.length % 2 === 1;
  }

  return fillRule === "evenodd"
    ? pointInContours(point, normalizedContours)
    : pointNonzeroInContours(point, normalizedContours);
}

function nestingChainAtPoint(point: Point, contours: Point[][]): Point[][] {
  const containing = contours.filter(contour => pointInPolygon(point, contour));
  if (containing.length <= 1) {
    return containing;
  }

  const sorted = [...containing].sort((left, right) => Math.abs(signedArea(right)) - Math.abs(signedArea(left)));
  for (let index = 0; index < sorted.length - 1; index += 1) {
    if (!contourParentsContour(sorted[index]!, sorted[index + 1]!)) {
      return [];
    }
  }

  return sorted;
}

export function contourInteriorPoint(points: Point[]): Point {
  return interiorPoint(points);
}

export function pointInPolygon(point: Point, polygon: Point[]): boolean {
  let inside = false;
  for (let index = 0, previousIndex = polygon.length - 1; index < polygon.length; previousIndex = index, index += 1) {
    const current = polygon[index]!;
    const previous = polygon[previousIndex]!;
    const intersects =
      current.y > point.y !== previous.y > point.y &&
      point.x < ((previous.x - current.x) * (point.y - current.y)) / (previous.y - current.y || Number.EPSILON) + current.x;
    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
}

export function contourContainsContour(container: Point[], candidate: Point[]): boolean {
  if (
    container.length < 3 ||
    candidate.length < 3 ||
    !boundsContainBounds(pointsBounds(container), pointsBounds(candidate)) ||
    !pointInPolygon(interiorPoint(candidate), container)
  ) {
    return false;
  }

  if (contoursIntersect(container, candidate)) {
    return false;
  }

  return candidate.every(point => pointInPolygon(point, container) || pointOnPolygonBoundary(point, container));
}

export function contourNestsContour(container: Point[], candidate: Point[]): boolean {
  if (
    container.length < 3 ||
    candidate.length < 3 ||
    !boundsContainBounds(pointsBounds(container), pointsBounds(candidate))
  ) {
    return false;
  }

  return pointInPolygon(interiorPoint(candidate), container);
}

export function contourFormsFragmentHole(container: Point[], candidate: Point[]): boolean {
  const containerArea = Math.abs(signedArea(container));
  const candidateArea = Math.abs(signedArea(candidate));
  if (
    container.length < 3 ||
    candidate.length < 3 ||
    candidateArea >= containerArea ||
    candidateArea / containerArea >= 0.3 ||
    !contoursIntersect(container, candidate)
  ) {
    return false;
  }

  if (contourOverlapShouldUnionFill(container, candidate)) {
    return false;
  }

  return contourSharedInteriorShouldBeEmpty(container, candidate);
}

export function contourParentsContour(container: Point[], candidate: Point[]): boolean {
  if (contoursIntersect(container, candidate)) {
    return false;
  }

  return contourNestsContour(container, candidate);
}

function contourOverlapShouldUnionFill(left: Point[], right: Point[]): boolean {
  return contourSharedInteriorSamples(left, right).some(sample =>
    pointFilledInContours(sample, [left, right], "nonzero"));
}

function contourSharedInteriorShouldBeEmpty(left: Point[], right: Point[]): boolean {
  return contourSharedInteriorSamples(left, right).some(sample =>
    !pointFilledInContours(sample, [left, right], "nonzero"));
}

function contourSharedInteriorSamples(left: Point[], right: Point[]): Point[] {
  const leftBounds = pointsBounds(left);
  const rightBounds = pointsBounds(right);
  const minX = Math.max(leftBounds.left, rightBounds.left);
  const maxX = Math.min(leftBounds.right, rightBounds.right);
  const minY = Math.max(leftBounds.top, rightBounds.top);
  const maxY = Math.min(leftBounds.bottom, rightBounds.bottom);
  if (minX > maxX || minY > maxY) {
    return [];
  }

  const samples: Point[] = [];
  const stepX = Math.max((maxX - minX) / 4, 0.5);
  const stepY = Math.max((maxY - minY) / 4, 0.5);
  for (let y = minY; y <= maxY; y += stepY) {
    for (let x = minX; x <= maxX; x += stepX) {
      const sample = { x, y };
      if (pointInPolygon(sample, left) && pointInPolygon(sample, right)) {
        samples.push(sample);
      }
    }
  }

  return samples;
}

export function normalizeNestedContourWindings(contours: Point[][]): Point[][] {
  const normalized = contours.map(contour => [...contour]);

  // Precompute signed areas and bounding boxes once. The parent search is
  // O(contours^2) and `contourParentsContour` runs segment-intersection tests,
  // so a cheap bounding-box rejection before it keeps this affordable on large
  // networks (this runs on every hover via `pointFilledInContours`).
  const signedAreas = normalized.map(contour => signedArea(contour));
  const absAreas = signedAreas.map(area => Math.abs(area));
  const bounds = normalized.map(contour => pointsBounds(contour));

  for (let childIndex = 0; childIndex < normalized.length; childIndex += 1) {
    const child = normalized[childIndex]!;
    const childArea = absAreas[childIndex]!;
    const childBounds = bounds[childIndex]!;
    let parentIndex: number | undefined;
    let parentArea = Number.POSITIVE_INFINITY;

    for (let candidateIndex = 0; candidateIndex < normalized.length; candidateIndex += 1) {
      if (candidateIndex === childIndex) {
        continue;
      }

      const candidateArea = absAreas[candidateIndex]!;
      if (candidateArea <= childArea || candidateArea >= parentArea) {
        continue;
      }

      if (!boundsContainBounds(bounds[candidateIndex]!, childBounds)) {
        continue;
      }

      if (!contourParentsContour(normalized[candidateIndex]!, child)) {
        continue;
      }

      parentIndex = candidateIndex;
      parentArea = candidateArea;
    }

    if (parentIndex === undefined) {
      continue;
    }

    if (Math.sign(signedAreas[parentIndex]!) === Math.sign(signedAreas[childIndex]!)) {
      normalized[childIndex] = [...child].reverse();
    }
  }

  return normalized;
}

export function signedArea(points: Point[]): number {
  return points.reduce((area, point, index) => {
    const next = points[(index + 1) % points.length]!;
    return area + point.x * next.y - next.x * point.y;
  }, 0) / 2;
}

export function contoursIntersect(left: Point[], right: Point[]): boolean {
  for (let leftIndex = 0; leftIndex < left.length; leftIndex += 1) {
    const leftFrom = left[leftIndex]!;
    const leftTo = left[(leftIndex + 1) % left.length]!;
    for (let rightIndex = 0; rightIndex < right.length; rightIndex += 1) {
      const rightFrom = right[rightIndex]!;
      const rightTo = right[(rightIndex + 1) % right.length]!;
      if (segmentsIntersect(leftFrom, leftTo, rightFrom, rightTo)) {
        return true;
      }
    }
  }

  return false;
}

function pointsBounds(points: Point[]): { left: number; top: number; right: number; bottom: number } {
  let left = Number.POSITIVE_INFINITY;
  let top = Number.POSITIVE_INFINITY;
  let right = Number.NEGATIVE_INFINITY;
  let bottom = Number.NEGATIVE_INFINITY;

  for (const point of points) {
    left = Math.min(left, point.x);
    top = Math.min(top, point.y);
    right = Math.max(right, point.x);
    bottom = Math.max(bottom, point.y);
  }

  return { left, top, right, bottom };
}

function boundsContainBounds(
  container: { left: number; top: number; right: number; bottom: number },
  candidate: { left: number; top: number; right: number; bottom: number },
): boolean {
  return (
    candidate.left >= container.left - 1e-9 &&
    candidate.right <= container.right + 1e-9 &&
    candidate.top >= container.top - 1e-9 &&
    candidate.bottom <= container.bottom + 1e-9
  );
}

function segmentsIntersect(a: Point, b: Point, c: Point, d: Point): boolean {
  const d1 = direction(a, b, c);
  const d2 = direction(a, b, d);
  const d3 = direction(c, d, a);
  const d4 = direction(c, d, b);

  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }

  return (
    (Math.abs(d1) <= 1e-9 && pointOnSegment(c, a, b)) ||
    (Math.abs(d2) <= 1e-9 && pointOnSegment(d, a, b)) ||
    (Math.abs(d3) <= 1e-9 && pointOnSegment(a, c, d)) ||
    (Math.abs(d4) <= 1e-9 && pointOnSegment(b, c, d))
  );
}

function pointOnPolygonBoundary(point: Point, polygon: Point[]): boolean {
  return polygon.some((from, index) => pointOnSegment(point, from, polygon[(index + 1) % polygon.length]!));
}

function pointOnSegment(point: Point, from: Point, to: Point): boolean {
  return (
    Math.abs(direction(from, to, point)) <= 1e-9 &&
    point.x >= Math.min(from.x, to.x) - 1e-9 &&
    point.x <= Math.max(from.x, to.x) + 1e-9 &&
    point.y >= Math.min(from.y, to.y) - 1e-9 &&
    point.y <= Math.max(from.y, to.y) + 1e-9
  );
}

function direction(a: Point, b: Point, c: Point): number {
  return (c.x - a.x) * (b.y - a.y) - (c.y - a.y) * (b.x - a.x);
}

function interiorPoint(points: Point[]): Point {
  const centroid = polygonCentroid(points);
  if (pointInPolygon(centroid, points)) {
    return centroid;
  }

  for (let index = 0; index < points.length; index += 1) {
    const a = points[index]!;
    const b = points[(index + 1) % points.length]!;
    const midpoint = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const candidate = { x: (midpoint.x + centroid.x) / 2, y: (midpoint.y + centroid.y) / 2 };
    if (pointInPolygon(candidate, points)) {
      return candidate;
    }
  }

  return points[0] ?? centroid;
}

function polygonCentroid(points: Point[]): Point {
  let area = 0;
  let x = 0;
  let y = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index]!;
    const next = points[(index + 1) % points.length]!;
    const factor = current.x * next.y - next.x * current.y;
    area += factor;
    x += (current.x + next.x) * factor;
    y += (current.y + next.y) * factor;
  }

  if (Math.abs(area) < 0.0000001) {
    return points[0] ?? { x: 0, y: 0 };
  }

  return { x: x / (3 * area), y: y / (3 * area) };
}

function tokenizePath(pathData: string): string[] {
  return pathData.match(/[a-zA-Z]|[-+]?(?:\d*\.\d+|\d+)(?:e[-+]?\d+)?/gi) ?? [];
}

function isPathCommand(token: string): boolean {
  return /^[a-zA-Z]$/.test(token);
}

function absolutize(point: Point, cursor: Point, relative: boolean): Point {
  return relative ? { x: cursor.x + point.x, y: cursor.y + point.y } : point;
}

function evaluateCubic(p0: Point, p1: Point, p2: Point, p3: Point, t: number): Point {
  const mt = 1 - t;
  return {
    x: mt ** 3 * p0.x + 3 * mt ** 2 * t * p1.x + 3 * mt * t ** 2 * p2.x + t ** 3 * p3.x,
    y: mt ** 3 * p0.y + 3 * mt ** 2 * t * p1.y + 3 * mt * t ** 2 * p2.y + t ** 3 * p3.y,
  };
}

function evaluateQuadratic(p0: Point, p1: Point, p2: Point, t: number): Point {
  const mt = 1 - t;
  return {
    x: mt * mt * p0.x + 2 * mt * t * p1.x + t * t * p2.x,
    y: mt * mt * p0.y + 2 * mt * t * p1.y + t * t * p2.y,
  };
}

function removeDuplicatePoints(points: Point[]): Point[] {
  const deduped: Point[] = [];
  for (const point of points) {
    const previous = deduped.at(-1);
    if (!previous || Math.hypot(previous.x - point.x, previous.y - point.y) > 0.001) {
      deduped.push(point);
    }
  }

  const first = deduped[0];
  const last = deduped.at(-1);
  if (first && last && Math.hypot(first.x - last.x, first.y - last.y) <= 0.001) {
    deduped.pop();
  }
  return deduped;
}


function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}
